import React from 'react';
import classnames from 'classnames/bind';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { Camera } from 'types/camera';
import { NavRoute } from 'application/types/routes';
import { VideoSource } from 'hooks/api/useDeploymentsVideoSources';

import { isStreamable } from 'services/stream';
import { useModal } from 'hooks/useModal';
import { useSnapshot } from 'hooks/useSnapshot';
import { useStreams } from 'streams/hooks/useStreams';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { Text } from 'components/Text/Text';
import { MediaPreview } from 'components/MediaPreview/MediaPreview';

import styles from './VideoSourceSnapshot.module.scss';

const c = classnames.bind(styles);

export type VideoSourceSnapshotProps = {
  className?: string;
  source?: VideoSource | null;
  /** this is only used to determine if this source is from a virtual camera */
  camera?: Camera | null;
  compact?: boolean;
  hideTitle?: boolean;
  autoRefresh?: boolean;
  showSettingsButton?: boolean;
  onSettingsClick?: () => void;
};

/** @deprecated */
export function VideoSourceSnapshot({
  className,
  source,
  camera,
  compact,
  hideTitle,
  autoRefresh,
  showSettingsButton,
  onSettingsClick,
}: VideoSourceSnapshotProps) {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const queryClient = useQueryClient();
  const timeoutRef = React.useRef<number>();

  const { pathname } = useLocation();
  const { open } = useModal();

  const isVirtualCamera = camera?.conn_type === 'virtual';
  const isStreamSource = source && 'stream_type' in source;

  // For virtual cameras, fetch the camera streams to get the latest stream
  const { data: virtualCameraStreams } = useStreams({
    params: {
      sources: ['camera_stream', 'uri_stream'],
      camera_ids: camera?.id ? [camera.id] : [],
    },
    enabled: isVirtualCamera && Boolean(camera?.id),
  });

  // snapshotSource is the source used for MediaPreview
  // For virtual cameras, use the latest stream from that camera
  // For regular sources, use the source itself
  const snapshotSource: VideoSource | null | undefined = React.useMemo(() => {
    if (isVirtualCamera) {
      if (virtualCameraStreams?.data[0]) {
        return virtualCameraStreams.data[0];
      }
      return null;
    }
    return source;
  }, [isVirtualCamera, virtualCameraStreams, source]);

  const { url, previousUrl, file, takeNewSnapshot, isLoading, error } =
    useSnapshot(snapshotSource);
  const date = file
    ? Intl.DateTimeFormat('en-us', {
        dateStyle: 'medium',
        timeStyle: 'medium',
      }).format(new Date(file.created_at))
    : undefined;

  const previousImage = React.useMemo(
    () => <img className={c('image')} src={previousUrl} />,
    [previousUrl]
  );

  const canTakeSnapShot = React.useMemo(() => {
    if (source && 'stream_type' in source) {
      return (
        (source.source === 'camera_stream' && source.status === 'online') ||
        source.source === 'uri_stream' ||
        (source.source === 'pipeline_stream' && source.stream_type === 'rtsp')
      );
    }

    return false;
  }, [source]);

  function handleTakeNewSnapshotClick() {
    if (isVirtualCamera) {
      // Creating a new snapshot from the same source (file stream) does not
      // make sense for virtual cameras as the file never changes its content.
      // For virtual cameras new file streams are added that needs to be refetched
      queryClient.invalidateQueries(['streams']);
    } else {
      setIsLoaded(false);
      takeNewSnapshot();
    }
  }

  React.useEffect(() => {
    if (
      autoRefresh &&
      source &&
      source.status === 'online' &&
      canTakeSnapShot &&
      !isLoading
    ) {
      // then setup a refresh every 5 mins
      timeoutRef.current = window.setInterval(() => {
        takeNewSnapshot();
      }, 300000);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [autoRefresh, source, takeNewSnapshot, isLoading, canTakeSnapShot]);

  function renderPreview() {
    if (!source) {
      return (
        <>
          <div className={c('image', 'placeholder')} />
          <div className={c('image-info', 'info')}>
            <Text>
              No motion events or streams available to create a snapshot.
            </Text>
            <Button size="small" onClick={handleTakeNewSnapshotClick}>
              Try again
            </Button>
          </div>
        </>
      );
    }

    if (!snapshotSource && isVirtualCamera) {
      return (
        <>
          <div className={c('image', 'placeholder')} />
          <div className={c('image-info', 'info')}>
            <Text>Waiting for motion events...</Text>
          </div>
        </>
      );
    }

    if (source.status === 'offline') {
      return (
        <>
          <div className={c('image', 'placeholder')} />
          <Text className={c('image-info', 'info')}>
            {'stream_type' in source ? 'Stream' : 'Camera'} is unavailable.
          </Text>
        </>
      );
    }

    if (error) {
      return (
        <>
          <div className={c('image', 'placeholder')} />
          <Text className={c('image-info', 'info')}>
            {error.message ||
              'An unknown error occurred. Please try again later.'}
          </Text>
        </>
      );
    }

    if (!url || file?.cloud_status !== 'uploaded') {
      return (
        <>
          {!compact && canTakeSnapShot && (
            <div className="loading-spinner-wrap">
              <span className="loading-spinner small" />
            </div>
          )}
          <div className={c('image-container')}>
            {isStreamSource && isStreamable(source) && (
              <MediaPreview className={c('snapshot-overlay')} stream={source} />
            )}
            <div className={c('image-wrap', 'placeholder')}>
              {previousUrl ? previousImage : <div className={c('image')} />}
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        {!isLoaded && !compact && canTakeSnapShot && (
          <div className="loading-spinner-wrap">
            <span className="loading-spinner small" />
          </div>
        )}
        <div className={c('image-container')}>
          {isStreamSource && isStreamable(source) && (
            <MediaPreview className={c('snapshot-overlay')} stream={source} />
          )}
          <div
            className={c('image-wrap', {
              placeholder: !isLoaded || !file || !url || !!true,
            })}
          >
            <img
              className={c('image')}
              src={url}
              alt={`${source.name} snapshot from ${date}`}
              onLoad={() => setIsLoaded(true)}
            />
          </div>
          {!compact && (
            <Text size="small" className={c('image-date')}>
              {date}
            </Text>
          )}
        </div>
      </>
    );
  }

  const isFileSearchEnabled =
    pathname.includes(NavRoute.MONITOR) &&
    source &&
    isStreamSource &&
    // We don't offer file search drawer for pipeline output streams because files are associated with the input stream
    source.source !== 'pipeline_stream';

  return (
    <figure className={c('wrap', className)}>
      <div className={c('image-wrap')}>{renderPreview()}</div>
      {!compact && (
        <div className={c('snapshot-info')}>
          <div className={c('snapshot-text')}>
            {!hideTitle && source && (
              <Tooltip content={source.name}>
                <Text size="small" className={c('snapshot-text-inner')}>
                  {source.name}
                </Text>
              </Tooltip>
            )}
          </div>
          <div className={c('snapshot-buttons')}>
            <ButtonGroup align="end">
              <>
                {isFileSearchEnabled && (
                  <IconButton
                    onClick={() =>
                      open('files-search', {
                        defaultStreamId: source.id,
                      })
                    }
                    label="Search recordings"
                    icon="search"
                    variant="tertiary"
                    size="small"
                  />
                )}
                {((source?.status === 'online' && canTakeSnapShot) ||
                  isVirtualCamera) && (
                  <IconButton
                    onClick={handleTakeNewSnapshotClick}
                    label="Take a new snapshot"
                    icon="take-snapshot"
                    variant="tertiary"
                    disabled={isLoading || !source}
                    size="small"
                  />
                )}
                {file && url && (
                  <IconButton
                    href={url}
                    label="Download file"
                    icon="download"
                    variant="tertiary"
                    size="small"
                  />
                )}
                {showSettingsButton && onSettingsClick && (
                  <IconButton
                    onClick={onSettingsClick}
                    label="Settings"
                    icon="settings"
                    variant="tertiary"
                    size="small"
                  />
                )}
              </>
            </ButtonGroup>
          </div>
        </div>
      )}
    </figure>
  );
}
